import { useState, useCallback, useRef } from 'react';
import {
  generateId,
  pointsNear,
  parseEquation,
  equationToLinePoints,
  parseCoordinateInput,
  parseCurveEquation,
  curvesToPoints,
  fitQuadratic,
  fitAbsoluteValue,
  fitSquareRoot,
  fitNthRoot,
  fitExponential,
  fitLogarithmic,
  fitSine,
  fitCosine,
  fitTangent,
  fitPolynomial,
  formatPolynomialEquation,
  getPolynomialPointsRequired,
  CURVE_TYPES,
  CURVE_POINTS_REQUIRED,
  isCurveMode,
  getCurvePointsRequired,
} from './coordinateUtils';

/**
 * Custom hook for managing coordinate grid state
 * @param {Object} initialValue - Initial value with elements array
 * @param {Object} config - Grid configuration
 * @returns {Object} - Grid state and actions
 */
// Generate points for curve elements that have parsed data but no points (e.g. from server extraction)
export const hydrateElements = (elements, gridConfig) => {
  if (!elements || elements.length === 0) return elements || [];
  return elements.map(el => {
    if (el.type === 'curve' && (!el.points || el.points.length < 2) && el.parsed) {
      const bounds = {
        xMin: gridConfig?.xMin ?? -10, xMax: gridConfig?.xMax ?? 10,
        yMin: gridConfig?.yMin ?? -10, yMax: gridConfig?.yMax ?? 10,
      };
      return { ...el, points: curvesToPoints(el.parsed, bounds) };
    }
    return el;
  });
};

const useCoordinateGrid = (initialValue = { elements: [] }, config = {}) => {
  const [elements, setElements] = useState(() => hydrateElements(initialValue?.elements, config?.grid));
  const [mode, setMode] = useState('point');
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [pendingPoints, setPendingPoints] = useState([]); // For line/polygon/circle construction

  const maxElements = config.maxElements || 20;
  const curveOptionsRef = useRef({});

  // Push current state to history for undo
  const pushHistory = useCallback(() => {
    setHistory((prev) => [...prev.slice(-19), elements]);
  }, [elements]);

  // Add a point element
  const addPoint = useCallback(
    (x, y, xLabel, yLabel) => {
      if (elements.length >= maxElements) return null;

      pushHistory();
      const newPoint = { type: 'point', x, y, id: generateId() };
      if (xLabel != null) newPoint.xLabel = xLabel;
      if (yLabel != null) newPoint.yLabel = yLabel;
      setElements((prev) => [...prev, newPoint]);
      return newPoint;
    },
    [elements.length, maxElements, pushHistory]
  );

  // Add a line element
  const addLine = useCallback(
    (startPoint, endPoint) => {
      if (elements.length >= maxElements) return null;

      pushHistory();
      const newLine = {
        type: 'line',
        points: [
          { x: startPoint.x, y: startPoint.y },
          { x: endPoint.x, y: endPoint.y },
        ],
        id: generateId(),
      };
      setElements((prev) => [...prev, newLine]);
      return newLine;
    },
    [elements.length, maxElements, pushHistory]
  );

  // Add a polygon element
  const addPolygon = useCallback(
    (points) => {
      if (points.length < 3) return null;
      if (elements.length >= maxElements) return null;

      pushHistory();
      const newPolygon = {
        type: 'polygon',
        points: points.map((p) => ({ x: p.x, y: p.y })),
        id: generateId(),
      };
      setElements((prev) => [...prev, newPolygon]);
      return newPolygon;
    },
    [elements.length, maxElements, pushHistory]
  );

  // Add a circle element
  const addCircle = useCallback(
    (center, radius) => {
      if (elements.length >= maxElements) return null;

      pushHistory();
      const newCircle = {
        type: 'circle',
        center: { x: center.x, y: center.y },
        radius,
        id: generateId(),
      };
      setElements((prev) => [...prev, newCircle]);
      return newCircle;
    },
    [elements.length, maxElements, pushHistory]
  );

  // Add a line element via equation
  const addEquationLine = useCallback(
    (equationString, gridBounds = { xMin: -10, xMax: 10, yMin: -10, yMax: 10 }) => {
      if (elements.length >= maxElements) {
        return { error: 'Maximum number of elements reached' };
      }

      const parsed = parseEquation(equationString);
      if (!parsed.valid) {
        return { error: parsed.error };
      }

      const points = equationToLinePoints(parsed, gridBounds);
      if (points.length !== 2) {
        return { error: 'Could not generate line points' };
      }

      pushHistory();
      const newLine = {
        type: 'equationLine',
        equation: equationString,
        parsed: parsed,
        points: points,
        id: generateId(),
      };
      setElements((prev) => [...prev, newLine]);
      return newLine;
    },
    [elements.length, maxElements, pushHistory]
  );

  // Add a point via typed coordinates (supports fractions and decimals)
  const addTypedPoint = useCallback(
    (inputString, gridBounds = { xMin: -10, xMax: 10, yMin: -10, yMax: 10 }) => {
      if (elements.length >= maxElements) {
        return { error: 'Maximum number of elements reached' };
      }

      const parsed = parseCoordinateInput(inputString);
      if (!parsed.valid) {
        return { error: parsed.error };
      }

      const { x, y, xLabel, yLabel } = parsed;
      const { xMin, xMax, yMin, yMax } = gridBounds;

      // Check bounds
      if (x < xMin || x > xMax) {
        return { error: `X value must be between ${xMin} and ${xMax}` };
      }
      if (y < yMin || y > yMax) {
        return { error: `Y value must be between ${yMin} and ${yMax}` };
      }

      return addPoint(x, y, xLabel, yLabel);
    },
    [elements.length, maxElements, addPoint]
  );

  // Add a curve element via equation
  const addCurveEquation = useCallback(
    (equationString, gridBounds = { xMin: -10, xMax: 10, yMin: -10, yMax: 10 }) => {
      if (elements.length >= maxElements) {
        return { error: 'Maximum number of elements reached' };
      }

      const parsed = parseCurveEquation(equationString);
      if (!parsed.valid) {
        return { error: parsed.error };
      }

      // Create flat params structure with type included
      const curveParams = { type: parsed.type, ...parsed.params };
      const points = curvesToPoints(curveParams, gridBounds);
      if (points.length < 2) {
        return { error: 'Could not generate curve points' };
      }

      pushHistory();
      const newCurve = {
        type: 'curve',
        equation: equationString,
        curveType: parsed.type,
        parsed: curveParams, // Store flat structure with type
        points: points,
        id: generateId(),
      };
      setElements((prev) => [...prev, newCurve]);
      return newCurve;
    },
    [elements.length, maxElements, pushHistory]
  );

  // Add a curve element from clicked points (curve fitting)
  const addCurveFromPoints = useCallback(
    (curveType, clickedPoints, gridBounds = { xMin: -10, xMax: 10, yMin: -10, yMax: 10 }, options = {}) => {
      if (elements.length >= maxElements) {
        return { error: 'Maximum number of elements reached' };
      }

      let fitResult;
      let params;

      switch (curveType) {
        case CURVE_TYPES.QUADRATIC:
          fitResult = fitQuadratic(clickedPoints);
          if (!fitResult.valid) {
            return { error: fitResult.error };
          }
          // fitResult.params contains { a, b, c }
          params = { type: CURVE_TYPES.QUADRATIC, ...fitResult.params };
          break;

        case CURVE_TYPES.ABSOLUTE:
          fitResult = fitAbsoluteValue(clickedPoints[0], clickedPoints[1]);
          if (!fitResult.valid) {
            return { error: fitResult.error };
          }
          params = { type: CURVE_TYPES.ABSOLUTE, ...fitResult.params };
          break;

        case CURVE_TYPES.SQRT:
          fitResult = fitSquareRoot(clickedPoints[0], clickedPoints[1]);
          if (!fitResult.valid) {
            return { error: fitResult.error };
          }
          params = { type: CURVE_TYPES.SQRT, ...fitResult.params };
          break;

        case CURVE_TYPES.NTH_ROOT: {
          const rootIndex = options?.nthRootIndex ?? config?.nthRootIndex ?? 3;
          fitResult = fitNthRoot(clickedPoints[0], clickedPoints[1], rootIndex);
          if (!fitResult.valid) {
            return { error: fitResult.error };
          }
          params = { type: CURVE_TYPES.NTH_ROOT, ...fitResult.params };
          break;
        }

        case CURVE_TYPES.EXPONENTIAL:
          fitResult = fitExponential(clickedPoints[0], clickedPoints[1], clickedPoints[2]);
          if (!fitResult.valid) {
            return { error: fitResult.error };
          }
          params = { type: CURVE_TYPES.EXPONENTIAL, ...fitResult.params };
          break;

        case CURVE_TYPES.LOGARITHMIC:
          fitResult = fitLogarithmic(clickedPoints[0], clickedPoints[1], options.logBase || 10);
          if (!fitResult.valid) {
            return { error: fitResult.error };
          }
          params = { type: CURVE_TYPES.LOGARITHMIC, ...fitResult.params };
          break;

        case CURVE_TYPES.SINE:
          fitResult = fitSine(clickedPoints[0], clickedPoints[1], clickedPoints[2]);
          if (!fitResult.valid) {
            return { error: fitResult.error };
          }
          params = { type: CURVE_TYPES.SINE, ...fitResult.params };
          break;

        case CURVE_TYPES.COSINE:
          fitResult = fitCosine(clickedPoints[0], clickedPoints[1], clickedPoints[2]);
          if (!fitResult.valid) {
            return { error: fitResult.error };
          }
          params = { type: CURVE_TYPES.COSINE, ...fitResult.params };
          break;

        case CURVE_TYPES.TANGENT:
          fitResult = fitTangent(clickedPoints[0], clickedPoints[1], clickedPoints[2]);
          if (!fitResult.valid) {
            return { error: fitResult.error };
          }
          params = { type: CURVE_TYPES.TANGENT, ...fitResult.params };
          break;

        case CURVE_TYPES.POLYNOMIAL:
          fitResult = fitPolynomial(clickedPoints);
          if (!fitResult.valid) {
            return { error: fitResult.error };
          }
          params = { type: CURVE_TYPES.POLYNOMIAL, ...fitResult.params };
          break;

        default:
          return { error: `Unknown curve type: ${curveType}` };
      }

      const points = curvesToPoints(params, gridBounds);
      if (points.length < 2) {
        return { error: 'Could not generate curve points' };
      }

      // Generate a readable equation string
      let equationStr = '';
      switch (curveType) {
        case CURVE_TYPES.QUADRATIC:
          equationStr = `y = ${params.a.toFixed(2)}x² + ${params.b.toFixed(2)}x + ${params.c.toFixed(2)}`;
          break;
        case CURVE_TYPES.ABSOLUTE:
          equationStr = `y = ${params.a.toFixed(2)}|x - ${params.h.toFixed(2)}| + ${params.k.toFixed(2)}`;
          break;
        case CURVE_TYPES.SQRT: {
          // Keep up to 4 decimals so fine grid steps (0.001) survive; strip trailing zeros.
          const fmt = (n) => (Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(4))));
          const inside = (params.b ?? 1) < 0
            ? `${fmt(params.h)} - x`
            : `x - ${fmt(params.h)}`;
          equationStr = `y = ${fmt(params.a)}√(${inside}) + ${fmt(params.k)}`;
          break;
        }
        case CURVE_TYPES.NTH_ROOT: {
          const rootLbl = params.n === 3 ? '∛' : `root(${params.n}, `;
          const close = params.n === 3 ? '' : ')';
          equationStr = `y = ${params.a.toFixed(2)}·${rootLbl}(x - ${params.h.toFixed(2)})${close} + ${params.k.toFixed(2)}`;
          break;
        }
        case CURVE_TYPES.EXPONENTIAL:
          equationStr = `y = ${params.a.toFixed(2)} × ${params.base.toFixed(2)}^x + ${params.c.toFixed(2)}`;
          break;
        case CURVE_TYPES.LOGARITHMIC: {
          const baseLabel = Math.abs(params.base - Math.E) < 1e-10 ? 'ln' : `log_${Number.isInteger(params.base) ? params.base : params.base.toFixed(2)}`;
          equationStr = `y = ${params.a.toFixed(2)}·${baseLabel}(x - ${params.h.toFixed(2)}) + ${params.k.toFixed(2)}`;
          break;
        }
        case CURVE_TYPES.SINE:
          equationStr = `y = ${params.a.toFixed(2)}sin(${params.b.toFixed(2)}x + ${params.c.toFixed(2)}) + ${params.d.toFixed(2)}`;
          break;
        case CURVE_TYPES.COSINE:
          equationStr = `y = ${params.a.toFixed(2)}cos(${params.b.toFixed(2)}x + ${params.c.toFixed(2)}) + ${params.d.toFixed(2)}`;
          break;
        case CURVE_TYPES.TANGENT:
          equationStr = `y = ${params.a.toFixed(2)}tan(${params.b.toFixed(2)}x + ${params.c.toFixed(2)}) + ${params.d.toFixed(2)}`;
          break;
        case CURVE_TYPES.POLYNOMIAL:
          equationStr = formatPolynomialEquation(params.coefficients);
          break;
        default:
          equationStr = 'curve';
      }

      pushHistory();
      const newCurve = {
        type: 'curve',
        equation: equationStr,
        curveType: curveType,
        parsed: params,
        points: points,
        clickedPoints: clickedPoints, // Store original clicked points for reference
        id: generateId(),
      };
      setElements((prev) => [...prev, newCurve]);
      return newCurve;
    },
    [elements.length, maxElements, pushHistory, config?.nthRootIndex]
  );

  // Add a curve point via typed coordinates (feeds pendingPoints, auto-fits when enough)
  const addTypedCurvePoint = useCallback(
    (inputString, gridBounds = { xMin: -10, xMax: 10, yMin: -10, yMax: 10 }) => {
      if (elements.length >= maxElements) {
        return { error: 'Maximum number of elements reached' };
      }

      const parsed = parseCoordinateInput(inputString);
      if (!parsed.valid) {
        return { error: parsed.error };
      }

      const { x, y } = parsed;
      const { xMin, xMax, yMin, yMax } = gridBounds;

      // Check bounds
      if (x < xMin || x > xMax) {
        return { error: `X value must be between ${xMin} and ${xMax}` };
      }
      if (y < yMin || y > yMax) {
        return { error: `Y value must be between ${yMin} and ${yMax}` };
      }

      const requiredCount = getCurvePointsRequired(mode);
      if (requiredCount === 0) {
        return { error: 'Not in a curve mode' };
      }

      const newPendingPoints = [...pendingPoints, { x, y }];

      if (newPendingPoints.length < requiredCount) {
        // Still collecting points
        setPendingPoints(newPendingPoints);
        return { pendingCount: newPendingPoints.length, requiredCount };
      }

      // Have all required points — fit the curve
      const curveType = mode.startsWith('polynomial') ? CURVE_TYPES.POLYNOMIAL : mode;
      const result = addCurveFromPoints(curveType, newPendingPoints, gridBounds, curveOptionsRef.current);
      setPendingPoints([]);

      if (result?.error) {
        return { error: result.error };
      }

      return { fitted: true };
    },
    [elements.length, maxElements, mode, pendingPoints, addCurveFromPoints]
  );

  // Remove an element by ID
  const removeElement = useCallback(
    (id) => {
      pushHistory();
      setElements((prev) => prev.filter((el) => el.id !== id));
      if (selectedId === id) {
        setSelectedId(null);
      }
    },
    [pushHistory, selectedId]
  );

  // Undo last action
  const undo = useCallback(() => {
    if (history.length === 0) return;

    const prevState = history[history.length - 1];
    setElements(prevState);
    setHistory((prev) => prev.slice(0, -1));
    setSelectedId(null);
    setPendingPoints([]);
  }, [history]);

  // Clear all elements
  const clear = useCallback(() => {
    if (elements.length === 0) return;

    pushHistory();
    setElements([]);
    setSelectedId(null);
    setPendingPoints([]);
  }, [elements.length, pushHistory]);

  // Handle click on grid
  const handleGridClick = useCallback(
    (gridPosition) => {
      const { x, y } = gridPosition;

      // Check if clicking on existing point
      const existingPoint = elements.find(
        (el) => el.type === 'point' && pointsNear({ x, y }, el, 0.3)
      );

      switch (mode) {
        case 'point':
          if (!existingPoint) {
            addPoint(x, y);
          }
          break;

        case 'line':
          if (pendingPoints.length === 0) {
            // First point of line
            setPendingPoints([{ x, y }]);
          } else {
            // Second point - create line
            addLine(pendingPoints[0], { x, y });
            setPendingPoints([]);
          }
          break;

        case 'polygon':
          // Check if clicking near first point to close polygon
          if (pendingPoints.length >= 3 && pointsNear({ x, y }, pendingPoints[0], 0.5)) {
            addPolygon(pendingPoints);
            setPendingPoints([]);
          } else {
            // Add point to polygon
            setPendingPoints((prev) => [...prev, { x, y }]);
          }
          break;

        case 'circle':
          if (pendingPoints.length === 0) {
            // First point is center
            setPendingPoints([{ x, y }]);
          } else {
            // Second point determines radius
            const center = pendingPoints[0];
            const radius = Math.sqrt(
              Math.pow(x - center.x, 2) + Math.pow(y - center.y, 2)
            );
            if (radius > 0) {
              addCircle(center, Math.round(radius * 100) / 100);
            }
            setPendingPoints([]);
          }
          break;

        // Curve modes
        case 'quadratic':
        case 'absolute':
        case 'sqrt':
        case 'nthRoot':
        case 'exponential':
        case 'logarithmic':
        case 'sine':
        case 'cosine':
        case 'tangent': {
          const requiredPoints = CURVE_POINTS_REQUIRED[mode];
          const newPendingPoints = [...pendingPoints, { x, y }];

          if (newPendingPoints.length < requiredPoints) {
            // Still collecting points
            setPendingPoints(newPendingPoints);
          } else {
            // Have all required points - fit the curve
            const gridBounds = config.grid || { xMin: -10, xMax: 10, yMin: -10, yMax: 10 };
            const result = addCurveFromPoints(mode, newPendingPoints, gridBounds, curveOptionsRef.current);
            if (result?.error) {
              // If fitting failed, keep points and let UI show error
              console.warn('Curve fitting failed:', result.error);
            }
            setPendingPoints([]);
          }
          break;
        }

        default:
          // Handle polynomial modes (polynomial2, polynomial3, polynomial4, polynomial5, etc.)
          if (mode.startsWith('polynomial')) {
            const degree = parseInt(mode.replace('polynomial', ''), 10);
            if (!isNaN(degree) && degree >= 1) {
              const requiredPoints = getPolynomialPointsRequired(degree);
              const newPendingPoints = [...pendingPoints, { x, y }];

              if (newPendingPoints.length < requiredPoints) {
                // Still collecting points
                setPendingPoints(newPendingPoints);
              } else {
                // Have all required points - fit the polynomial
                const gridBounds = config.grid || { xMin: -10, xMax: 10, yMin: -10, yMax: 10 };
                const result = addCurveFromPoints(CURVE_TYPES.POLYNOMIAL, newPendingPoints, gridBounds);
                if (result?.error) {
                  console.warn('Polynomial fitting failed:', result.error);
                }
                setPendingPoints([]);
              }
            }
          }
          break;
      }
    },
    [mode, elements, pendingPoints, addPoint, addLine, addPolygon, addCircle, addCurveFromPoints, config.grid]
  );

  // Cancel current operation (e.g., polygon construction)
  const cancelOperation = useCallback(() => {
    setPendingPoints([]);
  }, []);

  // Get current value for saving
  const getValue = useCallback(() => {
    return { elements };
  }, [elements]);

  // Set value externally (for loading)
  const setValue = useCallback((newValue) => {
    setElements(hydrateElements(newValue?.elements, config?.grid));
    setSelectedId(null);
    setPendingPoints([]);
    setHistory([]);
  }, [config?.grid]);

  return {
    // State
    elements,
    mode,
    selectedId,
    pendingPoints,
    canUndo: history.length > 0,

    // Setters
    setMode,
    setSelectedId,

    // Actions
    addPoint,
    addLine,
    addPolygon,
    addCircle,
    addEquationLine,
    addTypedPoint,
    addTypedCurvePoint,
    addCurveEquation,
    addCurveFromPoints,
    curveOptionsRef,
    removeElement,
    undo,
    clear,
    handleGridClick,
    cancelOperation,

    // Value management
    getValue,
    setValue,
  };
};

export default useCoordinateGrid;
